import type { DomainError } from '@core/domain/errors';
import { mapErrorCodeToCategory, getUserFriendlyMessage } from '@core/domain/errors';

/**
 * GraphQL error interface
 */
interface GraphQLErrorObject {
  graphQLErrors: Array<{
    message?: string;
    extensions?: {
      code?: string;
      details?: unknown;
      correlationId?: string;
    };
  }>;
}

/**
 * Type guard for GraphQL errors
 */
export function isGraphQLError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const errorObj = error as GraphQLErrorObject;

  return 'graphQLErrors' in errorObj && Array.isArray(errorObj.graphQLErrors);
}

/**
 * Adapts GraphQL errors to domain errors
 */
// eslint-disable-next-line complexity
export function adaptGraphQLError(error: unknown): DomainError {
  if (!isGraphQLError(error)) {
    throw new Error('Not a GraphQL error');
  }

  const graphQLError = (error as GraphQLErrorObject).graphQLErrors[0];

  const code = graphQLError?.extensions?.code || 'UNKNOWN_ERROR';
  const message = graphQLError?.message || 'An unexpected error occurred';
  const category = mapErrorCodeToCategory(code);

  const result = {
    code,
    message,
    userMessage: getUserFriendlyMessage(category, message, code),
    category,
    details: graphQLError?.extensions?.details,
    correlationId: graphQLError?.extensions?.correlationId,
  };

  return result;
}
